import { AsyncPipe } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy, inject } from '@angular/core';

import { AutomationDirective } from '@frontend/sports/automation/core-feature';
import { BetBuilderMarket, EventModel } from '@frontend/sports/betting-offer/feature/model';
import { DispatcherService } from '@frontend/sports/common/core/utils/dispatcher';
import { CustomActiveStateDirective } from '@frontend/sports/common/ui/custom-active-state';
import { HapticsDirective } from '@frontend/sports/haptic-feedback/feature';
import { NativePrice } from '@frontend/sports/odds/feature/native-price';
import { PickSourceProvider, SourceOfPick } from '@frontend/sports/option/pick/feature/tracking';
import { NOT_APPLICABLE, trackingConstants } from '@frontend/sports/tracking/feature';
import { Store } from '@ngrx/store';

import { ExternalBetslipActions } from '../../betslip/core/external-betslip-actions';
import { BetBuilderPickId } from '../../betslip/core/picks/pick-id';
import { BetBuilderTrackingValues } from '../bet-builder-core/bet-builder-tracking';
import { BaseBetslipEvents } from '../betslip-base/events/events';
import { SHEET_VIEW_OPEN } from '../sheet-view-core/sheet-view.token';
import { OptionIndicatorDirective } from './option-indicator.directive';
import { PickComponent } from './pick.component';

@Component({
    selector: 'ms-betbuilder-pick',
    templateUrl: './pick.component.html',
    imports: [CustomActiveStateDirective, OptionIndicatorDirective, AutomationDirective, AsyncPipe, HapticsDirective],
})
export class BetBuilderPickComponent extends PickComponent implements OnChanges, OnDestroy {
    private pickSourceProvider = inject(PickSourceProvider);
    private store = inject(Store);
    private dispatcher = inject(DispatcherService);

    @Input() event: EventModel;
    @Input() fixtureId: string;
    @Input() betBuilderMarket: BetBuilderMarket;
    @Input() contentPosition?: string;
    @Input() isFromPortal?: boolean;

    override pickId: BetBuilderPickId;

    readonly isInSheetView = Boolean(inject(SHEET_VIEW_OPEN, { optional: true }));

    get price(): NativePrice {
        return NativePrice.empty(); // left empty because BetBuilderMarket is not adapted to INativePrice
    }

    get priceId(): number | undefined {
        return this.betBuilderMarket.option.id;
    }

    private createPickId(): BetBuilderPickId {
        return new BetBuilderPickId(
            this.event.id,
            this.fixtureId,
            this.betBuilderMarket.id,
            this.betBuilderMarket.option.id,
            this.betBuilderMarket.option.optionPrice.id,
        );
    }

    ngOnChanges(): void {
        this.setPickId(this.createPickId(), undefined, this.betBuilderMarket?.option?.longId);
    }

    protected addPick(): void {
        const pickSource = this.pickSourceProvider.get(
            this.trackingSource,
            this.trackingPosition,
            false,
            this.marqueeName,
            this.sitecoreTemplateId,
            this.marqueeType,
            undefined,
            this.isAutomatedMarquee,
            this.isInSheetView,
        );

        pickSource.additional = pickSource.additional || {};
        pickSource.betDetails = this.betDetails;
        pickSource.moduleType = this.trackingModuleType;

        switch (this.sourceOfPick) {
            case SourceOfPick.EmbeddedBetBuilder:
                pickSource.betDetails = BetBuilderTrackingValues.SportcastSgpEmbedded;
                break;
            case SourceOfPick.OverlayBetBuilder:
                pickSource.betDetails = BetBuilderTrackingValues.SportcastSgpOverlay;
                break;
            case SourceOfPick.BetStationMarquee:
                pickSource.additional[trackingConstants.SPORT_ID] = this.event.sport.id.toString();
                pickSource.additional[trackingConstants.SPORT_LEAGUE_ID] = this.event.league.id.toString();
                pickSource.additional[trackingConstants.RESULT_ID] = this.betBuilderMarket.option.id.toString();
                pickSource.additional[trackingConstants.MARKET_ID] = this.betBuilderMarket.id.toString();
                pickSource.additional[trackingConstants.COMPONENT_CONTENT_POSITION] = this.trackingPosition?.toString() || NOT_APPLICABLE;
                pickSource.additional[trackingConstants.MARQUEE_NAME] = this.marqueeName || NOT_APPLICABLE;
                pickSource.additional[trackingConstants.MARQUEE_TYPE] = this.marqueeType || NOT_APPLICABLE;
                pickSource.additional[trackingConstants.SITECORE_TEMPLATE_ID] = this.sitecoreTemplateId || NOT_APPLICABLE;
                break;
        }

        const eventData = {
            event: {
                id: this.event.id,
                date: this.event.startDate,
                name: this.event.name,
                isLive: this.event.live,
                groupId: this.event.groupId,
            },
            league: {
                id: this.event.league.id,
                name: this.event.league.name,
                parentLeagueId: this.event.league.parentLeagueId,
            },
            region: {
                id: this.event.region.id,
                name: this.event.region.name,
            },
            offerSource: this.event.offerSource,
            betBuilderTradingV2FixtureId: this.event.betBuilderTradingV2FixtureId,
        };

        const longId = this.betBuilderMarket?.option?.longId;
        if (longId) {
            this.store.dispatch(
                ExternalBetslipActions.addEntainUiBetBuilderPick({
                    pickId: this.pickId,
                    tracking: pickSource,
                    eventData,
                    betBuilderOptionMarket: this.betBuilderMarket,
                    eventModel: this.event,
                    longId,
                }),
            );
        } else {
            this.store.dispatch(
                ExternalBetslipActions.addBetBuilderPick({
                    pickId: this.pickId,
                    tracking: pickSource,
                    eventData,
                    additionalInfo: {
                        sportcastId: this.event.sportcastId!,
                        betgeniusId: Number(this.event.getBetBuilderConsumerFixtureId()),
                        longIds: this.betBuilderMarket.longIds!,
                        useV2Key: !!this.event.betBuilderTradingV2FixtureId,
                    },
                }),
            );
        }
    }

    override pickClick(): void {
        if (this.isFromPortal) {
            this.dispatcher.dispatch(BaseBetslipEvents.HomeMarqueAddPick, this.pickId);
        }
        super.pickClick();
    }

    protected removePick(): void {
        const longId = this.betBuilderMarket?.option?.longId;
        if (longId) {
            this.store.dispatch(
                ExternalBetslipActions.removeEntainUiBetBuilderPick({
                    longId,
                    pickId: this.pickId,
                    eventId: this.pickId.eventId,
                }),
            );
        } else {
            this.store.dispatch(ExternalBetslipActions.removePick({ pickId: this.pickId }));
        }
    }
}
